import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  MapPin, CalendarDays, Plus, ChevronDown, UserPlus, Receipt, MessageSquare, RefreshCw, Package,
} from "lucide-react";
import { API } from "../../../App";
import { useTimezone } from "../../../contexts/TimezoneContext";
import JobPreviewModal from "../../jobs/JobPreviewModal";
import StoreUsageCard from "../StoreUsageCard";
import { getAuthConfig, SectionCard } from "./dashUtils";
import DashKpiRow from "./DashKpiRow";
import NeedsAttention from "./NeedsAttention";
import TodaysOperations from "./TodaysOperations";
import { LocationCards, LocationComparison } from "./LocationSection";
import PerformanceChart from "./PerformanceChart";
import { FinancialSnapshot, DocumentsSnapshot } from "./FinancialSnapshot";
import { CommunicationsCard, ActivityFeed } from "./CommsAndActivity";
import TechnicianOverview from "./TechnicianOverview";
import UpcomingTasksCard from "../UpcomingTasksCard";

const DATE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "custom", label: "Custom Range…" },
];

const prefsKey = (user) => `store_dash_prefs_${user?.user_id || user?.id || "anon"}`;

const Skeleton = () => (
  <div className="space-y-4" data-testid="store-dashboard-skeleton">
    <div className="flex gap-3">
      {[...Array(6)].map((_, i) => <div key={i} className="h-24 flex-1 rounded-xl bg-muted animate-pulse" />)}
    </div>
    <div className="h-16 rounded-xl bg-muted animate-pulse" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 h-80 rounded-xl bg-muted animate-pulse" />
      <div className="h-80 rounded-xl bg-muted animate-pulse" />
    </div>
  </div>
);

const StoreDashboardV2 = ({ user }) => {
  const navigate = useNavigate();
  const timezoneCtx = useTimezone();
  const getToday = timezoneCtx?.getToday || (() => new Date());

  const saved = (() => {
    try { return JSON.parse(localStorage.getItem(prefsKey(user)) || "{}"); } catch { return {}; }
  })();
  const [locationId, setLocationId] = useState(saved.locationId || "all");
  const [dateFilter, setDateFilter] = useState(saved.dateFilter || "today");
  const [customStart, setCustomStart] = useState(saved.customStart || "");
  const [customEnd, setCustomEnd] = useState(saved.customEnd || "");
  const [customOpen, setCustomOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [previewJob, setPreviewJob] = useState(null);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [sendingToTech, setSendingToTech] = useState(null);
  const [sendingReview, setSendingReview] = useState(null);
  const [live, setLive] = useState(false);
  const pollRef = useRef(null);
  const fetchDataRef = useRef(() => {});

  useEffect(() => {
    try {
      localStorage.setItem(prefsKey(user), JSON.stringify({ locationId, dateFilter, customStart, customEnd }));
    } catch { /* storage full */ }
  }, [locationId, dateFilter, customStart, customEnd, user]);

  const fetchData = useCallback(async (showLoader = true) => {
    if (dateFilter === "custom" && (!customStart || !customEnd)) return;
    if (showLoader && !data) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/store/dashboard-v2`, {
        ...getAuthConfig(),
        params: {
          location_id: locationId,
          date_filter: dateFilter,
          ...(dateFilter === "custom" ? { start_date: customStart, end_date: customEnd } : {}),
        },
      });
      setData(res.data);
      setError(null);
    } catch (e) {
      if (!data) setError(e.response?.data?.detail || e.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, dateFilter, customStart, customEnd]);

  useEffect(() => { fetchData(true); }, [fetchData]);
  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  // Backup polling — 60s when the live socket is down, 5min when live.
  useEffect(() => {
    pollRef.current = setInterval(() => fetchData(false), live ? 300000 : 60000);
    return () => clearInterval(pollRef.current);
  }, [fetchData, live]);

  // Live updates — WebSocket push (calls, messages, payments, jobs) with a
  // debounced silent refetch; auto-reconnects with backoff.
  useEffect(() => {
    let ws, closed = false, retry = 0, debounceTimer, reconnectTimer;
    const connect = () => {
      const token = localStorage.getItem("token");
      if (!token || closed) return;
      try {
        ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws/dashboard?token=${encodeURIComponent(token)}`);
      } catch { return; }
      ws.onopen = () => { retry = 0; setLive(true); };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type && msg.type !== "ping") {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => fetchDataRef.current(false), 1200);
          }
        } catch { /* ignore malformed frames */ }
      };
      ws.onclose = () => {
        setLive(false);
        if (!closed) {
          retry += 1;
          reconnectTimer = setTimeout(connect, Math.min(30000, 2000 * retry));
        }
      };
      ws.onerror = () => { try { ws.close(); } catch { /* already closed */ } };
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(debounceTimer);
      clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch { /* noop */ }
    };
  }, []);

  useEffect(() => {
    axios.get(`${API}/settings/payment-types`, getAuthConfig())
      .then((r) => setPaymentTypes(Array.isArray(r.data) ? r.data : r.data?.payment_types || []))
      .catch(() => {});
  }, []);

  const openJob = async (row) => {
    try {
      const res = await axios.get(`${API}/jobs/${row.job_id}`, getAuthConfig());
      setPreviewJob(res.data);
    } catch {
      toast.error("Could not open job");
    }
  };

  const handleSendReminder = async (job) => {
    try {
      await axios.post(`${API}/notifications/send-reminder`, {
        job_id: job.job_id, customer_phone: job.customer_phone, customer_name: job.customer_name,
        job_title: job.title, scheduled_date: job.scheduled_date, scheduled_time: job.scheduled_time,
      }, getAuthConfig());
      toast.success("Reminder sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reminder");
    }
  };
  const handleSendToTech = async (job) => {
    if (!job.assigned_to) { toast.error("No technician assigned to this job"); return; }
    setSendingToTech(job.job_id);
    try {
      const res = await axios.post(`${API}/technicians/jobs/${job.job_id}/send-assignment`, {}, getAuthConfig());
      if (res.data?.masking_warning) toast.warning(res.data.masking_warning, { duration: 9000 });
      toast.success("Job sent to technician");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send job");
    } finally { setSendingToTech(null); }
  };
  const handleSendReviewRequest = async (job) => {
    setSendingReview(job.job_id);
    try {
      await axios.post(`${API}/jobs/${job.job_id}/send-review-request`, {}, getAuthConfig());
      toast.success("Review request sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send review request");
    } finally { setSendingReview(null); }
  };
  const handleDelete = async (job) => {
    if (!window.confirm(`Delete job "${job.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/jobs/${job.job_id}`, getAuthConfig());
      toast.success("Job deleted");
      fetchData(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete job");
    }
  };
  const handleRename = async (job, title) => {
    try {
      await axios.patch(`${API}/jobs/${job.job_id}/title`, { title }, getAuthConfig());
      setPreviewJob((prev) => (prev && prev.job_id === job.job_id ? { ...prev, title } : prev));
      toast.success("Job renamed");
      fetchData(false);
    } catch { toast.error("Failed to rename job"); }
  };

  if (loading && !data) return <Skeleton />;
  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3" data-testid="store-dashboard-error">
        <p className="text-foreground font-semibold">Could not load dashboard</p>
        <p className="text-muted-foreground text-sm">{error}</p>
        <Button size="sm" onClick={() => fetchData(true)} className="bg-blue-600 hover:bg-blue-700 text-white">Retry</Button>
      </div>
    );
  }

  const d = data || {};
  const locations = d.locations || [];
  const multiLocation = locations.length > 1;
  const fa = !!d.financial_access;
  let today;
  try { today = getToday(); } catch { today = new Date(); }
  const periodLabel = dateFilter === "custom" && customStart
    ? `${customStart} → ${customEnd}`
    : DATE_OPTIONS.find((o) => o.value === dateFilter)?.label || "Today";

  return (
    <div className="space-y-4 sm:space-y-5" data-testid="store-dashboard-v2">
      {/* ── Header ── */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground font-['Manrope']">
            Good {today.getHours() < 12 ? "morning" : today.getHours() < 17 ? "afternoon" : "evening"}, {user?.name?.split(" ")[0]}
            {live && (
              <span className="inline-flex items-center gap-1 ml-3 align-middle text-[10px] font-medium text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded-full" data-testid="dash-live-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
              </span>
            )}
            {refreshing && <RefreshCw className="inline w-4 h-4 ml-2 animate-spin text-muted-foreground/50" />}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="dash-subheader">
            {d.business_name && <span className="font-medium text-foreground/80">{d.business_name}</span>}
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {locations.length} Location{locations.length !== 1 ? "s" : ""}</span>
            <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {periodLabel}, {format(today, "MMMM d, yyyy")}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Location filter */}
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="dash-location-filter">
              <MapPin className="w-3.5 h-3.5 mr-1 text-blue-500 shrink-0" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* Date filter */}
          <Select value={dateFilter} onValueChange={(v) => { setDateFilter(v); if (v === "custom") setCustomOpen(true); }}>
            <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="dash-date-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DATE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {dateFilter === "custom" && (
            <Popover open={customOpen} onOpenChange={setCustomOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9" data-testid="dash-custom-range-btn">
                  {customStart ? `${customStart} → ${customEnd || "…"}` : "Pick dates"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 space-y-2" align="end">
                <p className="text-xs font-medium text-foreground">Custom range</p>
                <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full h-8 rounded-md border border-border bg-background px-2 text-sm" data-testid="dash-custom-start" />
                <input type="date" value={customEnd} min={customStart} onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full h-8 rounded-md border border-border bg-background px-2 text-sm" data-testid="dash-custom-end" />
                <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={!customStart || !customEnd}
                  onClick={() => setCustomOpen(false)} data-testid="dash-custom-apply">Apply</Button>
              </PopoverContent>
            </Popover>
          )}
          {/* Quick actions */}
          <Button size="sm" className="h-9 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => navigate("/dashboard/jobs/new")} data-testid="dash-create-job">
            <Plus className="w-4 h-4 mr-1" /> Create Job
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-9 px-2" data-testid="dash-quick-actions"><ChevronDown className="w-4 h-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate("/dashboard/customers")} data-testid="qa-customer"><UserPlus className="w-3.5 h-3.5 mr-2" /> New Customer</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/dashboard/invoices")} data-testid="qa-invoice"><Receipt className="w-3.5 h-3.5 mr-2" /> Create Invoice</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/dashboard/messages")} data-testid="qa-message"><MessageSquare className="w-3.5 h-3.5 mr-2" /> New Message</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/dashboard/inventory")} data-testid="qa-inventory"><Package className="w-3.5 h-3.5 mr-2" /> Inventory</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ── KPIs ── */}
      <DashKpiRow kpis={d.kpis} financialAccess={fa} loading={loading} />

      {/* ── Needs attention ── */}
      <NeedsAttention items={d.needs_attention} />

      {/* ── Operations + right column ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 min-w-0 space-y-4">
          <TodaysOperations operations={d.operations} onOpenJob={openJob} financialAccess={fa} />
          <LocationCards locations={d.location_performance} selected={d.selected_location} onSelect={setLocationId} financialAccess={fa} />
          {locationId === "all" && <LocationComparison locations={d.location_performance} financialAccess={fa} onSelect={setLocationId} />}
          <PerformanceChart locationId={locationId} multiLocation={multiLocation} />
        </div>
        <div className="min-w-0 space-y-4">
          <UpcomingTasksCard variant="store" />
          {fa && <FinancialSnapshot financial={d.financial} onRetry={() => fetchData(true)} />}
          <CommunicationsCard comms={d.communications} locationFiltered={locationId !== "all"} onRetry={() => fetchData(true)} />
          <TechnicianOverview technicians={d.technicians} financialAccess={fa} />
          <DocumentsSnapshot documents={d.documents} outstanding={{ count: d.needs_attention?.find((a) => a.type === "unpaid_invoices")?.count || 0, overdue_count: d.needs_attention?.find((a) => a.type === "overdue_invoices")?.count || 0 }} financialAccess={fa} />
          <ActivityFeed activity={d.activity} financialAccess={fa} />
        </div>
      </div>

      {/* ── Plan usage (compact, bottom) ── */}
      <SectionCard title="Plan Usage" testid="plan-usage-section">
        <StoreUsageCard />
      </SectionCard>

      {/* Job preview modal — keeps the user on the dashboard */}
      {previewJob && (
        <JobPreviewModal
          job={previewJob}
          onClose={() => { setPreviewJob(null); fetchData(false); }}
          onEdit={(job) => navigate(`/dashboard/jobs/${job.job_id}/edit`)}
          onDelete={handleDelete}
          onSendReminder={handleSendReminder}
          onSendToTech={handleSendToTech}
          onSendReviewRequest={handleSendReviewRequest}
          onNewJobForCustomer={(job) => navigate("/dashboard/jobs/new", { state: { customerPrefill: { customer_id: job.customer_id || "", customer_name: job.customer_name || "", customer_phone: job.customer_phone || "", customer_email: job.customer_email || "" } } })}
          onOpenCustomer={(job) => job.customer_id && navigate(`/dashboard/customers?open=${job.customer_id}`)}
          sendingToTech={sendingToTech}
          sendingReview={sendingReview}
          paymentTypes={paymentTypes}
          onRename={handleRename}
        />
      )}
    </div>
  );
};

export default StoreDashboardV2;
